import { Workbook } from 'exceljs';
import { fetchScsPanelReportRecords } from '../../service/scs-panel-report';
import type { FetchFormResponse } from '../../service/types';
import { downloadExcelBuffer } from '../../utils/list-page-excel/workbook';
import { getScsBusinessObjectives } from '../../utils/scs-objectives-pdf';

const headers = [
  'Full Name',
  'Email Address',
  'TDA (Y/N)',
  'Date of TDA',
  'Grade',
  'Line Manager Name',
  'Business unit',
  'Business obj 1 ',
  'Business Obj 2 ',
  'Business Obj 3 ',
  'Busin Obj 4 ',
  'Min Standards - Leadership ',
  'Min Standards - Finance ',
  'Min Standards - Corporate Activity ',
  'Min Standard - D&I ',
  ...[1, 2, 3, 4].flatMap((quarter) => [
    `Q${quarter} Indicative marking`,
    `Q${quarter} Standards met`,
  ]),
  'Job holder Summary ',
  'Line Manager Summary ',
  'Talent marking',
];

const text = (value: unknown): string => {
  const result = value == null ? '' : Array.isArray(value) ? value.join(', ') : String(value);
  if (result.length > 32767) throw new Error('A response exceeds the Excel cell limit.');
  return result;
};

export const buildScsPanelWorkbook = (
  records: FetchFormResponse[],
  year: string,
  quarter: string,
) => {
  if (!year || !/^Quarter [1-4]$/.test(quarter)) {
    throw new Error('Select a reporting year and quarter.');
  }
  if (!records.length) throw new Error('No responses match these filters.');
  const objectiveCount = Math.max(
    4,
    ...records.map((record) => getScsBusinessObjectives(record.answers || {}).length),
  );
  const extraHeaders = Array.from(
    { length: objectiveCount - 4 },
    (_, index) => `Business Obj ${index + 5}`,
  );
  const reportHeaders = [...headers.slice(0, 11), ...extraHeaders, ...headers.slice(11)];
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('Sheet1');
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  const widths = [
    13.67,
    16,
    12,
    12,
    12,
    17.33,
    12,
    11.33,
    11.33,
    11.33,
    11.33,
    21,
    26.67,
    26.67,
    26.67,
    ...[1, 2, 3, 4].flatMap(() => [26.67, 31.33]),
    10.56,
    10.56,
    11.56,
  ];
  const reportWidths = [
    ...widths.slice(0, 11),
    ...extraHeaders.map(() => 11.33),
    ...widths.slice(11),
  ];
  sheet.columns = reportHeaders.map((header, index) => ({ header, width: reportWidths[index] }));
  sheet.pageSetup.orientation = 'landscape';
  for (const record of records) {
    const answers = record.answers;
    if (!answers || text(answers['report-period']) !== year) {
      throw new Error('Responses have changed. Refresh the admin page and try again.');
    }
    const objectives = getScsBusinessObjectives(answers);
    const approval = (number: number) => ({
      ...answers,
      ...record.approvals?.[`Q${number}-${year}`]?.answers,
    });
    const selectedQuarter = Number(quarter.slice(-1));
    const selectedApproval = approval(selectedQuarter);
    const temporary = answers['temporary-promotion'];
    const onTemporaryPromotion =
      temporary === 'Yes' || answers['person-details-scs-grade'] === 'Temporary promotion';
    const values = [
      answers['person-details-scs-name'],
      answers['person-details-scs-email-address'],
      onTemporaryPromotion ? 'Y' : temporary === 'No' ? 'N' : '',
      answers['person-details-scs-grade-temporary-promotion-dates'],
      onTemporaryPromotion ? answers['temporary-grade'] : answers['person-details-scs-grade'],
      answers['person-details-manager-email'],
      answers['business-area'] === 'Other / Not listed'
        ? answers['person-details-business-unit-other']
        : answers['business-area'],
      ...Array.from({ length: objectiveCount }, (_, index) => {
        const objective = objectives[index];
        return objective ? `What: ${objective.what}\nHow: ${objective.how}` : '';
      }),
      answers['minimum-standard-leadership'],
      answers['minimum-standard-finance'],
      answers['minimum-standard-corporate'],
      answers['minimum-standard-diverityinclusion'],
      ...[1, 2, 3, 4].flatMap((number) => {
        const source = approval(number);
        const standards = source[`talent-career-minimum-standards-met-q${number}`];
        return [
          source[`talent-career-indicative-performance-q${number}`],
          standards === 'Yes' ? 'Met' : standards === 'No' ? 'Not Met' : standards,
        ];
      }),
      answers[`performance-summary-q${selectedQuarter}`],
      selectedApproval[`talent-career-managers-assessment-q${selectedQuarter}`],
      selectedApproval[`talent-career-manager-9-box-grid-q${selectedQuarter}`],
    ];
    sheet.addRow(values.map(text));
  }
  sheet.eachRow((row) => {
    row.font = { name: 'Calibri', size: 11 };
    row.height = sheet.properties.defaultRowHeight;
  });
  sheet.getColumn(4).numFmt = 'dd/mm/yyyy';
  sheet.getColumn(24 + extraHeaders.length).alignment = { wrapText: true };
  sheet.getColumn(25 + extraHeaders.length).alignment = { wrapText: true };
  sheet.autoFilter = { from: 'A1', to: { row: sheet.rowCount, column: reportHeaders.length } };
  return workbook;
};

export const downloadScsPanelReport = async (
  token: string,
  formIds: string[],
  year: string,
  quarter: string,
) => {
  const records = await fetchScsPanelReportRecords(token, formIds, year);
  records.sort((a, b) =>
    text(a.answers?.['person-details-scs-name']).localeCompare(
      text(b.answers?.['person-details-scs-name']),
    ),
  );
  const workbook = buildScsPanelWorkbook(records, year, quarter);
  const buffer = await workbook.xlsx.writeBuffer();
  const now = new Date();
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  downloadExcelBuffer(buffer as ArrayBuffer, `${date}_moderation_report.xlsx`);
};
